import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Music2, MapPin, Sparkles, Guitar } from "lucide-react";
import { HERO, CATEGORY_TILE } from "@/data/products";

const Eyebrow = ({ children, light }) => (
  <p className={`font-mono text-[11px] uppercase tracking-[0.22em] ${light ? "text-denim-accent" : "text-denim"}`}>{children}</p>
);

const VALUES = [
  { icon: MapPin, title: "New York Roots, 1984", copy: "Born in New York and defined by no one — an authentic American point of view stitched into every piece." },
  { icon: Sparkles, title: "The Art of Embellishment", copy: "Puff print, embroidery, chenille, rhinestone and applique — graphic craft is at the heart of the line." },
  { icon: Guitar, title: "An Authority on Denim", copy: "Signature fits and washes, from raw indigo to washed black, engineered for real-life wear." },
  { icon: Music2, title: "Rooted in Culture", copy: "A brand with a connection to music, festivals and the energy of American street style." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative h-[58vh] min-h-[440px] bg-navy overflow-hidden">
        <img src={HERO.home} alt="AR / American Rag campaign" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col justify-end pb-14 lg:pb-20">
          <Eyebrow light>Our Story</Eyebrow>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-7xl font-bold uppercase leading-[0.9] tracking-tight text-white max-w-3xl">
            AR / American Rag
          </h1>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24 text-center">
        <p className="font-heading text-xl sm:text-2xl lg:text-3xl font-medium leading-snug text-ink text-balance">
          AR / American Rag is a contemporary American fashion brand that marries <span className="text-denim">California cool</span> with <span className="text-denim">East Coast chic</span> — denim-first, graphic-led, and unmistakably its own.
        </p>
        <p className="mt-6 text-muted-foreground text-base leading-relaxed">
          Fashionably fresh while staying true to its heritage, the line melds vintage-inspired graphics and authentic washes with premium embellishment and the season's latest trends.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16 lg:pb-24 grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
        <div className="relative aspect-[4/5] overflow-hidden bg-sand order-2 lg:order-1">
          <img src={CATEGORY_TILE["women-tees"]} alt="AR / American Rag women's tee" className="w-full h-full object-cover object-top" />
        </div>
        <div className="order-1 lg:order-2">
          <Eyebrow>Defined by no one</Eyebrow>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight leading-[0.95]">
            Rooted in American culture
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            AR / American Rag has consistently delivered trend-right, strong price-to-value product — the pieces that drive the floor, with an appeal that reaches far beyond a single customer.
          </p>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Uniquely authentic, with an eye to the future and a past rooted in popular American culture, it's a brand well known for its connection to music and festivals — an authority on trend and denim for anyone who wants fashion entirely on their own terms.
          </p>
          <Link to="/new-originals" data-testid="about-shop-cta" className="mt-8 inline-flex items-center gap-2 bg-ink text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-navy transition-colors">
            View New Originals <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <section className="bg-offwhite border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="text-center mb-12">
            <Eyebrow>What we stand for</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">The AR / American Rag spirit</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {VALUES.map((v) => (
              <div key={v.title} data-testid={`value-${v.title}`} className="bg-white border border-border p-7 hover:shadow-[0_12px_40px_rgba(15,30,54,0.08)] transition-shadow">
                <div className="w-11 h-11 bg-denim-light text-denim flex items-center justify-center rounded-full">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-heading font-bold text-base leading-tight">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-navy text-white overflow-hidden">
        <img src={HERO.homeAlt} alt="" className="absolute inset-0 w-full h-full object-cover object-top opacity-25" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-28 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[0.95]">
            Fashion on your<br />own terms.
          </h2>
          <p className="mt-5 text-white/70 max-w-xl mx-auto">Trend right. Denim first. Unmistakably AR / American Rag.</p>
          <Link to="/women-denim" className="mt-8 inline-flex items-center gap-2 bg-white text-ink px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-ink hover:text-white transition-colors">
            Explore Denim <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
